// frontend/src/services/scaleAgentService.ts
import { EventEmitter } from '../utils/EventEmitter';
import { ScaleAgentConfig } from '../types/scaleAgent';


class ScaleAgentService extends EventEmitter {
  private ws: WebSocket | null = null;
  private config: ScaleAgentConfig | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  
  public status = { 
    connected: false, 
    reconnectAttempts: 0,
    lastError: null as string | null,
    lastConnectedAt: null as Date | null
  };


  async connect(config: ScaleAgentConfig): Promise<void> {
    try {
      this.config = config;
      this.status.connected = false;
      this.status.reconnectAttempts = 0;
      
      // Fermer la connexion existante si elle existe
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
      
      // Créer une nouvelle connexion WebSocket
      this.ws = new WebSocket(config.wsUrl);
      
      // Gérer l'ouverture de la connexion
      this.ws.onopen = () => {
        this.status.connected = true;
        this.status.reconnectAttempts = 0;
        this.status.lastConnectedAt = new Date();
        this.status.lastError = null;
        this.emit('connected', config);
        this.emit('status_changed', this.status);
      };
      
      // Gérer la fermeture de la connexion
      this.ws.onclose = (event) => {
        this.status.connected = false;
        
        if (event.code !== 1000) { // Fermeture non normale
          this.emit('disconnected');
          this.emit('status_changed', this.status);
          
          // Tentative de reconnexion automatique
          this.attemptReconnect();
        }
      };
      
      // Gérer les erreurs de connexion
      this.ws.onerror = (error) => {
        this.status.lastError = 'Erreur de connexion WebSocket';
        this.emit('connection_error', error);
        this.emit('status_changed', this.status);
      };
      
      // Gérer les messages reçus
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Émettre l'événement weight_data avec les données reçues
          this.emit('weight_data', data);
          
          // Mettre à jour le statut
          this.status.lastError = null;
          this.emit('status_changed', this.status);
          
        } catch (error) {
          this.emit('error', new Error('Erreur parsing message WebSocket'));
        }
      };
      
    } catch (error) {
      this.status.lastError = error instanceof Error ? error.message : 'Erreur inconnue';
      this.emit('connection_error', error);
      this.emit('status_changed', this.status);
      throw error;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('error', new Error('Impossible de se reconnecter après plusieurs tentatives'));
      return;
    }

    this.reconnectAttempts++;
    this.status.reconnectAttempts = this.reconnectAttempts;
     
    this.reconnectTimeout = setTimeout(() => {
      if (this.config) {
        this.connect(this.config).catch(error => {
          console.error('❌ Échec reconnexion:', error);
        });
      }
    }, this.reconnectDelay);
  }

  disconnect(): void {
    
    // Arrêter la reconnexion automatique
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Fermer la connexion WebSocket
    if (this.ws) {
      this.ws.close(1000, 'Déconnexion normale');
      this.ws = null;
    }
    
    // Réinitialiser le statut
    this.status.connected = false;
    this.status.reconnectAttempts = 0;
    this.status.lastError = null;
    this.status.lastConnectedAt = null;
    
    this.emit('disconnected');
    this.emit('status_changed', this.status);
  }

  async sendCommand(command: any): Promise<any> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket non connecté');
    }

    try {
      this.ws.send(JSON.stringify(command));
      
      // Retourner une promesse qui se résout immédiatement
      // car WebSocket est asynchrone et nous ne pouvons pas attendre la réponse
      return { success: true, message: 'Commande envoyée' };
      
    } catch (error) {
      console.error('❌ Erreur envoi commande:', error);
      throw new Error('Erreur lors de l\'envoi de la commande');
    }
  }

  // Vérifier si la connexion est active
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // Obtenir l'état de la connexion WebSocket
  getReadyState(): number {
    return this.ws?.readyState || WebSocket.CLOSED;
  }
}

export const scaleAgentService = new ScaleAgentService();